// Pure display-level dedup for the franchisee map/home list.
// Extracted from ``FranchiseeTerritoryWidget.jsx`` so it can be unit-
// tested in isolation. See the block comment inside the widget's
// data-load ``useEffect`` for the full rationale — this module hosts
// the reducer only.
//
// Contract:
//   dedupeHomesForDisplay([...homes]) → [...homes]
//     * Tier-1: drop exact ``locationId`` collisions (belt-and-braces
//       against sync races / stale merges).
//     * Tier-2: within each normalised postcode, cluster rows whose
//       normalised names are equal OR one is a prefix of the other
//       ("Hazel Lodge" vs "Hazel Lodge Care Home"). Each cluster
//       becomes ONE displayed row — the one with the highest
//       completeness score.
//   Row order is preserved for singletons and cluster survivors so
//   downstream sorting still gets a predictable input.
//
// Completeness score (higher wins):
//   +100 if ``active !== false`` and not ``inactive``
//   +10  if ``numberOfBeds > 0``
//   +1   if ``gacServiceTypes`` non-empty
//   +1   if ``specialisms`` non-empty
//   +len(name)/100 (capped) — longer names usually mean the fuller
//                             registration ("... Care Home" > bare).

const _normName = (s) => String(s || "").toLowerCase().replace(/[\W_]+/g, "");
const _normPost = (s) => String(s || "").toUpperCase().replace(/\s+/g, "");

function _scoreHome(h) {
  let score = 0;
  if (h.active !== false && !h.inactive) score += 100;
  const beds = Number(h.numberOfBeds);
  if (Number.isFinite(beds) && beds > 0) score += 10;
  if ((h.gacServiceTypes || []).length > 0) score += 1;
  if ((h.specialisms || []).length > 0) score += 1;
  score += Math.min(String(h.name || "").length, 60) / 100;
  return score;
}

// Generic care-home descriptor tails that, when appended to a shorter
// name, still describe the SAME physical home rather than a distinct
// named unit. These are the only tails that trigger the prefix branch
// of the same-postcode dedupe. Anything else — e.g. "Proactive
// Assessment Unit", "Day Service", "Annexe", a person's name, a ward
// label — is a distinct service registered separately at the same
// address and MUST render as its own row.
//
// Regression driver: at TN18 5EF, both "Hawkhurst House" and
// "Hawkhurst House Proactive Assessment Unit" were being collapsed
// because the shorter name is a raw prefix of the longer one. The
// delta ("proactiveassessmentunit") is NOT on this allowlist, so the
// two rows now stay separate. The approved 13 dual-registrations
// (Hazel Lodge / Hazel Lodge Care Home, etc.) either share the same
// normalised name outright (equality branch — still collapses) or
// only differ by one of these generic descriptors (allowlist branch
// — still collapses). See __tests__/dedupeHomesForDisplay.test.js.
const _GENERIC_TAIL_ALLOWLIST = new Set([
  "carehome",
  "carecentre",
  "carecenter",
  "nursinghome",
  "residentialhome",
  "residentialcarehome",
  "nursingandresidentialhome",
  "residentialandnursinghome",
]);

// Decide whether two normalised names should be treated as the same
// physical home. Equal names always match. Prefix matches only fire
// when the appended tail is a whitelisted generic descriptor.
function _sameSiteName(a, b) {
  if (!a || !b) return false;
  if (a === b) return true;
  let shorter = a, longer = b;
  if (a.length > b.length) { shorter = b; longer = a; }
  if (!longer.startsWith(shorter)) return false;
  const tail = longer.slice(shorter.length);
  return _GENERIC_TAIL_ALLOWLIST.has(tail);
}

export function dedupeHomesForDisplay(rawHomes) {
  const seenLocationIds = new Set();
  const tier1 = [];
  for (const h of rawHomes || []) {
    const lid = h && h.locationId;
    if (lid) {
      if (seenLocationIds.has(lid)) continue;
      seenLocationIds.add(lid);
    }
    tier1.push(h);
  }

  const byPostcode = new Map();
  const noPostcode = [];
  for (const h of tier1) {
    const pc = _normPost(h.postalCode);
    if (!pc) { noPostcode.push(h); continue; }
    if (!byPostcode.has(pc)) byPostcode.set(pc, []);
    byPostcode.get(pc).push(h);
  }

  const out = [...noPostcode];
  for (const [, bucket] of byPostcode) {
    if (bucket.length === 1) { out.push(bucket[0]); continue; }
    const clusters = [];
    for (const h of bucket) {
      const n = _normName(h.name);
      let joined = false;
      for (const cluster of clusters) {
        for (const member of cluster) {
          const mn = _normName(member.name);
          if (_sameSiteName(n, mn)) {
            cluster.push(h); joined = true; break;
          }
        }
        if (joined) break;
      }
      if (!joined) clusters.push([h]);
    }
    for (const cluster of clusters) {
      if (cluster.length === 1) { out.push(cluster[0]); continue; }
      let best = cluster[0];
      let bestScore = _scoreHome(best);
      for (let i = 1; i < cluster.length; i += 1) {
        const s = _scoreHome(cluster[i]);
        if (s > bestScore) { best = cluster[i]; bestScore = s; }
      }
      out.push(best);
    }
  }
  return out;
}
